/**
 * Chairman Summary Report Page
 * Displays comprehensive summary statistics for the Chairman
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import chairmanReportsService from '../services/chairmanReportsService';
import { toast } from 'react-toastify';

const ChairmanSummaryReportPage = () => {
  const { user } = useAuth();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    countryId: '',
    jobRoleId: ''
  });

  useEffect(() => {
    if (user?.role === 'CHAIRMAN') {
      fetchSummaryData();
    }
  }, [user, filters]);

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await chairmanReportsService.getSummaryReport(filters);
      
      if (response.success) {
        setSummaryData(response.data);
      } else {
        setError('Failed to load summary data');
      }
    } catch (err) {
      console.error('Error fetching summary data:', err);
      setError(err.response?.data?.error || 'Failed to load summary data');
      toast.error('Failed to load summary data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      countryId: '',
      jobRoleId: ''
    });
  };

  if (user?.role !== 'CHAIRMAN') {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to Chairman users.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Summary Report</h1>
          <p className="text-gray-600">Comprehensive overview of all business metrics</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                name="countryId"
                value={filters.countryId}
                onChange={handleFilterChange}
                placeholder="Country ID"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Role</label>
              <input
                type="text"
                name="jobRoleId"
                value={filters.jobRoleId}
                onChange={handleFilterChange}
                placeholder="Job Role ID"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading summary data...</p>
          </div>
        ) : summaryData ? (
          <div className="space-y-8">
            {/* Interview Statistics */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {summaryData.summary?.interview?.acceptedCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">Accepted</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {summaryData.summary?.interview?.rejectedCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">Rejected</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {summaryData.summary?.interview?.redirectedCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">Redirected</div>
                </div>
              </div>
            </div>

            {/* Exam Statistics */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Exam Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {summaryData.summary?.exam?.passCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">Passed</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {summaryData.summary?.exam?.failCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">Failed</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {summaryData.summary?.exam?.reExamCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">Re-exam</div>
                </div>
              </div>
            </div>

            {/* Departure Statistics */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Departure Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {summaryData.summary?.departure?.departedCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">Departed</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {summaryData.summary?.departure?.noShowCount || 0}
                  </div>
                  <div className="text-sm text-gray-600">No Show</div>
                </div>
              </div>
            </div>

            {/* Payment Statistics */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    ${summaryData.summary?.payment?.totalRevenue || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    ${summaryData.summary?.payment?.applicantPayment || 0}
                  </div>
                  <div className="text-sm text-gray-600">Applicant Payment</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    ${summaryData.summary?.payment?.corporatePayment || 0}
                  </div>
                  <div className="text-sm text-gray-600">Corporate Payment</div>
                </div>
              </div>
            </div>

            {/* Report Metadata */}
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
              <p><strong>Generated At:</strong> {new Date(summaryData.generatedAt).toLocaleString()}</p>
              <p><strong>Filters Applied:</strong> {
                Object.entries(summaryData.filters || {})
                  .filter(([key, value]) => value)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(', ') || 'None'
              }</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No summary data available</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ChairmanSummaryReportPage;